import React, { useState, useEffect, useRef } from 'react';
import { useClickOutside } from '@/hooks/useClickOutside';
// import TriangleDownIcon from '@/assets/icons/triangle-down.svg?react';
import CloseIcon from '@/assets/icons/close-x.svg?react';
import SearchIcon from '@/assets/icons/search.svg?react';
import styles from './EditableDropdown.module.scss';

export interface IDropdownOption {
  id: string;
  label: string;
}

interface IEditableDropdownProps {
  label?: string;
  placeholder?: string;
  initialId: string | number;
  options: IDropdownOption[];
  // eslint-disable-next-line no-unused-vars
  onChange: (id: string) => void;
  type?: 'tile' | 'rectangle';
  variant?: 'normal' | 'large' | 'extra' | 'xxl' | 'xxxl' | 'xxxxl';
  alignment?: 'left' | 'center' | 'right';
  className?: string;
  allowClear?: boolean;
}

function EditableDropdown({
  label,
  placeholder: placeholderText,
  initialId,
  options,
  onChange,
  type = 'tile',
  variant = 'normal',
  alignment = 'center',
  className = '',
  allowClear = false,
}: IEditableDropdownProps) {
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<IDropdownOption[]>([]);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<IDropdownOption | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initialOption = options.find((opt) => opt.id === initialId);
    setInputValue(initialOption?.label || '');
    setFilteredOptions(options);
    setSelectedOption(initialOption || null);
  }, [initialId, options]);

  const handleOnClick = () => {
    setIsOpen(true); // enable dropdown when on click
    setFilteredOptions(options); // reset available options
    if (selectedOption) {
      const selectedIndex = options.findIndex((opt) => opt.id === selectedOption.id);
      setHighlightIndex(selectedIndex);
      requestAnimationFrame(() => scrollToItem(selectedIndex));
    }
  };

  const handleOnBlur = () => {
    if (isOpen) {
      const matchedOption = options.find((opt) => opt.label.toLowerCase() === inputValue.toLowerCase());
      // if input does not match a valid selection, clear it
      if (!matchedOption) {
        if (selectedOption) {
          setInputValue(selectedOption.label); // restore previous selection
        } else {
          setInputValue(''); // no previous selection, clear input
        }
      }
      // reset state
      setFilteredOptions(options);
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  useClickOutside(wrapperRef, isOpen ? filteredOptions.length : null, () => handleOnBlur());

  const scrollToItem = (index: number) => {
    const ul = wrapperRef.current?.querySelector('ul');
    const li = ul?.children[index] as HTMLElement | undefined;
    li?.scrollIntoView({ block: 'center', inline: 'nearest' });
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);

    const exactMatchIndex = options.findIndex((opt) => opt.label.toLowerCase() === value.toLowerCase());

    if (exactMatchIndex >= 0) {
      // show full list and highlight the exact match
      setFilteredOptions(options);
      setHighlightIndex(exactMatchIndex);
      requestAnimationFrame(() => scrollToItem(exactMatchIndex));
    } else {
      // show only partial matches
      const filtered = options.filter((opt) => opt.label.toLowerCase().includes(value.toLowerCase()));
      setFilteredOptions(filtered);
      if (filtered.length > 0) {
        setHighlightIndex(0);
        requestAnimationFrame(() => scrollToItem(0));
      } else {
        setHighlightIndex(-1);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filteredOptions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => {
        const next = (prev + 1) % filteredOptions.length;
        scrollToItem(next);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => {
        const next = prev <= 0 ? filteredOptions.length - 1 : prev - 1;
        scrollToItem(next);
        return next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < filteredOptions.length) {
        handleOptionClick(filteredOptions[highlightIndex]);
      }
    }
  };

  const handleOptionClick = (option: IDropdownOption) => {
    setInputValue(option.label);
    setSelectedOption(option); // update selection
    onChange(option.id); // return id
    setIsOpen(false);
    setHighlightIndex(-1); // reset highlight index
  };

  const handleClear = () => {
    setInputValue('');
    setSelectedOption(null);
    onChange('');
    setIsOpen(false);
    setHighlightIndex(-1); // reset highlight index
  };

  const showRectangleSearch = type === 'rectangle';
  const showClear = (type === 'rectangle' || allowClear) && !!selectedOption;
  const placeholder = placeholderText ?? (type === 'rectangle' && label ? `Search ${label}s` : undefined);

  return (
    <div ref={wrapperRef} className={`${styles.editableDropdown} ${styles[type]} ${styles[variant]} ${className}`}>
      {type === 'tile' && <div className={styles.label}>{label}</div>}
      <div className={styles.inputValueWrapper}>
        {showRectangleSearch && <SearchIcon className={styles.searchIcon} />}
        <input
          name="editableDropdown"
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            handleInputChange(e.target.value);
            setIsOpen(true); // enable dropdown when on change
          }}
          onClick={handleOnClick}
          onKeyDown={handleKeyDown}
          className={styles[alignment]}
        />
        {showClear && <CloseIcon className={styles.clearIcon} onClick={handleClear} />}
        {/* {type === 'tile' && <TriangleDownIcon className={styles.triangleIcon} />} */}
      </div>
      {isOpen && (
        <ul className={styles.dropdown}>
          {filteredOptions.length > 0 &&
            filteredOptions.map((option, index) => (
              <li key={option.id} className={index === highlightIndex ? styles.highlight : ''} onMouseEnter={() => setHighlightIndex(index)} onClick={() => handleOptionClick(option)}>
                {option.label}
              </li>
            ))}
          {!filteredOptions.length && <li className={styles.noData}>No Match</li>}
        </ul>
      )}
    </div>
  );
}

export default EditableDropdown;
